import dayjs from 'dayjs';
import type { ServiceOrderDto, ServiceOrderLineDto } from '../services/serviceOrderService';
import {
  calcGrossBeforeDiscount,
  calcGrossTotal,
  calcNetTotal,
  getBusinessLines,
  getDiscountGrossAmount,
  getDiscountLine,
  hasOrderDiscount,
  lineGrossAmount,
  lineTaxRateDisplay,
} from './serviceOrderTotals';

export type ServiceOrderAccountPreview = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyName?: string;
} | null;

export type ServiceOrderVehiclePreview = {
  vinChassis?: string;
  engineNo?: string;
} | null;

/** Parça satırı: ürün markası en başta (örn. Bridgestone - KOD123 - Ürün adı). */
export function formatServiceOrderPartLineDescription(line: {
  productBrandName?: string | null;
  productCode?: string | null;
  productName?: string | null;
}): string {
  const brand = line.productBrandName?.trim();
  const rest = [line.productCode?.trim(), line.productName?.trim()].filter(Boolean).join(' - ');
  if (brand && rest) return `${brand} - ${rest}`;
  if (brand) return brand;
  return rest || '—';
}

function getLineDescForPrint(line: ServiceOrderLineDto): string {
  if (line.itemType === 1) return line.serviceItemName || '—';
  if (line.itemType === 2) return formatServiceOrderPartLineDescription(line);
  if (line.itemType === 3) return line.externalServiceItemName || '—';
  return '—';
}

/** İskonto tablo dışında: ara toplam → iskonto → genel toplam */
export function buildDiscountSummaryBelowTable(lines: ServiceOrderLineDto[], fmt: (n: number) => string): string {
  const discount = getDiscountGrossAmount(getDiscountLine(lines));
  if (discount <= 0) return '';

  const grossBefore = calcGrossBeforeDiscount(lines);
  const grossFinal = calcGrossTotal(lines);

  return `
  <div class="fiche-discount-summary" style="max-width:340px;margin-left:auto;margin-top:12px;margin-bottom:16px;padding:12px 14px;border:1px solid #ddd;border-radius:6px;background:#fafafa;">
    <div style="display:flex;justify-content:space-between;gap:16px;margin-bottom:6px;font-size:13px;">
      <span>Ara toplam (KDV dahil)</span><span style="white-space:nowrap;">${fmt(grossBefore)}</span>
    </div>
    <div style="display:flex;justify-content:space-between;gap:16px;margin-bottom:8px;font-size:13px;color:#cf1322;">
      <span>İskonto</span><span style="white-space:nowrap;">−${fmt(discount)}</span>
    </div>
    <div style="display:flex;justify-content:space-between;gap:16px;font-size:14px;font-weight:700;border-top:2px solid #2c3e50;padding-top:8px;">
      <span>Genel toplam (KDV dahil)</span><span style="white-space:nowrap;">${fmt(grossFinal)}</span>
    </div>
  </div>`;
}

export function buildPrintTableFooterRow(lines: ServiceOrderLineDto[], fmt: (n: number) => string): string {
  const businessLines = getBusinessLines(lines);
  if (hasOrderDiscount(lines)) {
    const netBefore = calcNetTotal(businessLines);
    const grossBefore = calcGrossBeforeDiscount(lines);
    return `<tr class="total-row" style="background:#f8f9fa;font-weight:600;">
        <td colspan="5" style="text-align:right;padding-right:12px;padding:12px 6px;border-top:2px solid #2c3e50;font-size:13px;">Ara Toplam</td>
        <td style="text-align:right;padding:12px 6px;border-top:2px solid #2c3e50;font-size:13px;">${fmt(netBefore)}</td>
        <td style="text-align:right;padding:12px 6px;border-top:2px solid #2c3e50;font-size:13px;">${fmt(grossBefore)}</td>
      </tr>`;
  }
  const toplamHariç = calcNetTotal(lines);
  const toplamDahil = calcGrossTotal(lines);
  return `<tr class="total-row" style="background:#f8f9fa;font-weight:600;">
        <td colspan="5" style="text-align:right;padding-right:12px;padding:12px 6px;border-top:2px solid #2c3e50;font-size:13px;">Genel Toplam</td>
        <td style="text-align:right;padding:12px 6px;border-top:2px solid #2c3e50;font-size:13px;">${fmt(toplamHariç)}</td>
        <td style="text-align:right;padding:12px 6px;border-top:2px solid #2c3e50;font-size:13px;">${fmt(toplamDahil)}</td>
      </tr>`;
}

function buildModalTableFooterRow(lines: ServiceOrderLineDto[], fmt: (n: number) => string): string {
  const businessLines = getBusinessLines(lines);
  if (hasOrderDiscount(lines)) {
    const netBefore = calcNetTotal(businessLines);
    const grossBefore = calcGrossBeforeDiscount(lines);
    return `<tr class="total-row">
        <td colspan="6" class="label">Ara toplam</td>
        <td class="num">${fmt(netBefore)}</td>
        <td class="num">${fmt(grossBefore)}</td>
      </tr>`;
  }
  const toplamHariç = calcNetTotal(lines);
  const toplamDahil = calcGrossTotal(lines);
  return `<tr class="total-row">
        <td colspan="6" class="label">Genel toplam</td>
        <td class="num">${fmt(toplamHariç)}</td>
        <td class="num">${fmt(toplamDahil)}</td>
      </tr>`;
}

/**
 * Yazdır ve önizleme için aynı HTML gövdesi (Servis Onarım Teklif Formu).
 * @param forPrint true ise pencere açılınca yazdırma script'i eklenir.
 */
export function buildServiceOrderFicheDocumentHtml(opts: {
  fullOrder: ServiceOrderDto;
  accountData: ServiceOrderAccountPreview;
  vehicleData: ServiceOrderVehiclePreview | undefined;
  origin: string;
  forPrint: boolean;
}): string {
  const { fullOrder, accountData, vehicleData, origin, forPrint } = opts;

  const customerName = fullOrder.customerName || '';
  const customerPhone = fullOrder.customerPhone || accountData?.phone || '';
  const customerFirstName = accountData?.firstName || '';
  const customerLastName = accountData?.lastName || '';
  const customerFullName = [customerFirstName, customerLastName].filter(Boolean).join(' ') || customerName;
  const companyName = accountData?.companyName || '';

  const plate = fullOrder.vehiclePlate || '';
  const vehicleInfo = fullOrder.vehicleInfo || '';
  const vinChassis = vehicleData?.vinChassis || '';
  const engineNo = vehicleData?.engineNo || '';
  const entryKm = fullOrder.entryKm;

  const orderNo = fullOrder.orderNo || '';
  const orderDate = fullOrder.orderDate ? dayjs(fullOrder.orderDate) : dayjs();
  const dateStr = orderDate.format('DD.MM.YYYY');
  const timeStr = orderDate.format('HH:mm');
  const deliveredByUserName = fullOrder.deliveredByUserName || '';

  const complaintNotes = fullOrder.complaintNotes || '';
  const complaintItems = fullOrder.complaintItems || [];
  const complaintText =
    complaintItems.length > 0
      ? complaintItems.map((item: { name: string }) => item.name).join(', ') + (complaintNotes ? ` - ${complaintNotes}` : '')
      : complaintNotes;

  const orderLines = fullOrder.lines || [];
  const tableLines = getBusinessLines(orderLines);
  const fmt = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺';

  const rows = tableLines
    .map((line, i) => {
      const totalDahil = lineGrossAmount(line);
      const kdvPct = lineTaxRateDisplay(line);
      return `<tr>
          <td style="text-align:center;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${i + 1}</td>
          <td style="padding:8px 6px;border-bottom:1px solid #e8e8e8;">${getLineDescForPrint(line)}</td>
          <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${(line.quantity || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
          <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${(line.unitPrice || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
          <td style="text-align:center;padding:8px 6px;border-bottom:1px solid #e8e8e8;">%${kdvPct}</td>
          <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${fmt(line.totalPrice || 0)}</td>
          <td style="text-align:right;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${fmt(totalDahil)}</td>
        </tr>`;
    })
    .join('');

  const printScript = forPrint
    ? `<script>
    window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; };
  </script>`
    : '';

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Servis Fişi</title>
  <style>
    @page { size: A4; margin: 15mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', system-ui, sans-serif; margin: 0; padding: 15mm; color: #1a1a1a; font-size: 12px; }
    .logo-area { text-align: center; margin-bottom: 16px; }
    .logo-area img { width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block; }
    .page-break-logo { text-align: center; margin-bottom: 16px; page-break-before: always; break-before: page; }
    .page-break-logo img { width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block; }
    .content-wrapper { }
    .title { text-align: center; font-size: 24px; font-weight: bold; margin: 16px 0 20px 0; color: #1a1a1a; }
    .order-info { text-align: left; margin-bottom: 16px; font-size: 13px; }
    .info-row { display: flex; gap: 20px; margin-bottom: 16px; }
    .info-col { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .info-col h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    .info-item { margin-bottom: 6px; font-size: 12px; }
    .info-item strong { color: #495057; margin-right: 6px; }
    .complaint-section { margin: 16px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .complaint-section h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0 20px 0; }
    th { text-align: left; padding: 10px 6px; background: #2c3e50; color: #fff; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
    th:first-child { border-radius: 6px 0 0 0; }
    th:last-child { border-radius: 0 6px 0 0; }
    td { font-size: 12px; }
    .total-row { background: #f8f9fa; font-weight: 600; }
    .total-row td { padding: 12px 6px; border-top: 2px solid #2c3e50; font-size: 13px; }
    .signature-row { display: flex; gap: 20px; margin-top: 24px; page-break-inside: avoid; break-inside: avoid; }
    .signature-box { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .signature-box h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    .signature-box p { margin: 8px 0 0 0; font-size: 12px; }
    .warning-section { padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f5f5f5; }
    .warning-section h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #495057; }
    .warning-section p { margin: 0; font-size: 11px; color: #6c757d; line-height: 1.5; }
    .footer-section { padding-top: 24px; }
  </style>
</head>
<body>
  <div class="logo-area" style="text-align: center; margin-bottom: 16px;">
    <img src="${origin}/images/logo-print-full.png" alt="Logo" style="width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block;" onerror="this.style.display='none'">
  </div>
  <div class="content-wrapper">
  <div class="title">Servis Onarım Teklif Formu</div>
  <div class="order-info">
    <strong>Servis Fiş No:</strong> ${orderNo} - <strong>Tarih:</strong> ${dateStr} - <strong>Saat:</strong> ${timeStr}
  </div>
  <div class="info-row" style="display: flex; gap: 20px; margin-bottom: 16px;">
    <div class="info-col" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Müşteri Bilgileri</h4>
      <div class="info-item"><strong>Ad Soyad:</strong> ${customerFullName || '—'}</div>
      ${customerPhone ? `<div class="info-item"><strong>Telefon:</strong> ${customerPhone}</div>` : ''}
    </div>
    <div class="info-col" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Araç Bilgileri</h4>
      <div class="info-item"><strong>Plaka:</strong> ${plate || '—'}</div>
      ${vehicleInfo ? `<div class="info-item"><strong>Marka/Model:</strong> ${vehicleInfo}</div>` : ''}
      ${vinChassis ? `<div class="info-item"><strong>Şasi No:</strong> ${vinChassis}</div>` : ''}
      ${engineNo ? `<div class="info-item"><strong>Motor No:</strong> ${engineNo}</div>` : ''}
      ${entryKm ? `<div class="info-item"><strong>Km:</strong> ${entryKm.toLocaleString('tr-TR')}</div>` : ''}
    </div>
  </div>
  ${complaintText ? `
  <div class="complaint-section" style="margin: 16px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
    <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50;">Müşteri Şikayetleri</h4>
    <div>${complaintText}</div>
  </div>
  ` : ''}
  <table style="width: 100%; border-collapse: collapse; margin: 16px 0 20px 0;">
    <thead>
      <tr>
        <th style="width:36px;text-align:left;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-radius:6px 0 0 0;">#</th>
        <th style="text-align:left;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Kalem</th>
        <th style="width:72px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Adet</th>
        <th style="width:88px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Birim Fiyat</th>
        <th style="width:52px;text-align:center;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">KDV</th>
        <th style="width:100px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;">Toplam (KDV Hariç)</th>
        <th style="width:100px;text-align:right;padding:10px 6px;background:#2c3e50;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;border-radius:0 6px 0 0;">Toplam (KDV Dahil)</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      ${buildPrintTableFooterRow(orderLines, fmt)}
    </tbody>
  </table>
  ${buildDiscountSummaryBelowTable(orderLines, fmt)}
  <div class="signature-row" style="display: flex; gap: 20px; margin-top: 24px; page-break-inside: avoid; break-inside: avoid;">
    <div class="signature-box" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Teslim Eden</h4>
      <p style="margin: 8px 0 0 0; font-size: 12px;">${deliveredByUserName || '—'}</p>
    </div>
    <div class="signature-box" style="flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px;">Teslim Alan</h4>
      <p style="margin: 8px 0 0 0; font-size: 12px;">${customerFullName || '—'}${companyName ? `<br>${companyName}` : ''}</p>
    </div>
  </div>
  </div>
  <div class="footer-section" style="padding-top: 24px;">
    <div class="warning-section" style="padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f5f5f5;">
      <h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #495057;">Uyarı</h4>
      <p style="margin: 0; font-size: 11px; color: #6c757d; line-height: 1.5;">Araç teslimi yalnızca mesai saatleri dâhilinde gerçekleştirilebilir. Bakım ve onarım süresi boyunca, gerekli görülmesi halinde, teşhis ve kalite kontrolü amacıyla test sürüşü yapılabilir.</p>
    </div>
  </div>
  ${printScript}
</body>
</html>`;
}

/** Çoklu tablo varsa (yazdırma) sonraki tablolardan önce logo sayfa kırılımı ekler */
export function insertLogoBeforeAdditionalTables(html: string, origin: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const bodyEl = doc.body;
  if (!bodyEl) return html;
  const contentWrapper = bodyEl.querySelector('.content-wrapper');
  if (!contentWrapper) return html;
  const tables = contentWrapper.querySelectorAll('table');
  tables.forEach((table, index) => {
    if (index > 0) {
      const logoDiv = doc.createElement('div');
      logoDiv.className = 'page-break-logo';
      logoDiv.setAttribute('style', 'text-align: center; margin-bottom: 16px; page-break-before: always; break-before: page;');
      logoDiv.innerHTML = `<img src="${origin}/images/logo-print-full.png" style="width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block;" alt="Logo" onerror="this.style.display='none'">`;
      table.parentNode?.insertBefore(logoDiv, table);
    }
  });
  return '<!DOCTYPE html>\n' + doc.documentElement.outerHTML;
}

/**
 * Liste modal önizlemesi: logo, imza, uyarı yok; kalem tablosu ve özet bilgi.
 * `theme` uygulama teması ile eşleşir (data-theme + CSS değişkenleri).
 */
export function buildServiceOrderFicheModalPreviewHtml(opts: {
  fullOrder: ServiceOrderDto;
  accountData: ServiceOrderAccountPreview;
  theme: 'light' | 'dark';
}): string {
  const { fullOrder, accountData, theme } = opts;

  const customerName = fullOrder.customerName || '';
  const customerPhone = fullOrder.customerPhone || accountData?.phone || '';
  const customerFirstName = accountData?.firstName || '';
  const customerLastName = accountData?.lastName || '';
  const customerFullName = [customerFirstName, customerLastName].filter(Boolean).join(' ') || customerName;

  const plate = fullOrder.vehiclePlate || '';
  const vehicleInfo = fullOrder.vehicleInfo || '';

  const orderNo = fullOrder.orderNo || '';
  const orderDate = fullOrder.orderDate ? dayjs(fullOrder.orderDate) : dayjs();
  const dateStr = orderDate.format('DD.MM.YYYY HH:mm');
  const statusText = fullOrder.statusText || '—';

  const complaintNotes = fullOrder.complaintNotes || '';
  const complaintItems = fullOrder.complaintItems || [];
  const complaintText =
    complaintItems.length > 0
      ? complaintItems.map((item: { name: string }) => item.name).join(', ') + (complaintNotes ? ` — ${complaintNotes}` : '')
      : complaintNotes;

  const orderLines = fullOrder.lines || [];
  const tableLines = getBusinessLines(orderLines);
  const fmt = (n: number) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺';

  const rows = tableLines
    .map((line, i) => {
      const totalDahil = lineGrossAmount(line);
      const kdvPct = lineTaxRateDisplay(line);
      const desc = getLineDescForPrint(line);
      const typeLabel = line.itemTypeText || '—';
      return `<tr>
        <td class="num">${i + 1}</td>
        <td class="type">${typeLabel}</td>
        <td class="desc">${desc}</td>
        <td class="num">${(line.quantity || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
        <td class="num">${(line.unitPrice || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</td>
        <td class="num kdv">%${kdvPct}</td>
        <td class="num">${fmt(line.totalPrice || 0)}</td>
        <td class="num">${fmt(totalDahil)}</td>
      </tr>`;
    })
    .join('');

  const metaParts = [
    `<span class="meta-k">Fiş</span> ${orderNo || '—'}`,
    `<span class="meta-k">Tarih</span> ${dateStr}`,
    `<span class="meta-k">Durum</span> ${statusText}`,
  ];
  const whoParts = [
    `<span class="meta-k">Müşteri</span> ${customerFullName || '—'}`,
    customerPhone ? `<span class="meta-k">Tel</span> ${customerPhone}` : '',
    `<span class="meta-k">Plaka</span> ${plate || '—'}`,
    vehicleInfo ? `<span class="meta-k">Araç</span> ${vehicleInfo}` : '',
  ].filter(Boolean);

  return `<!DOCTYPE html>
<html lang="tr" data-theme="${theme}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Servis fişi özeti</title>
  <style>
    :root {
      --bg: #fafafa;
      --surface: #ffffff;
      --text: rgba(0, 0, 0, 0.88);
      --muted: rgba(0, 0, 0, 0.55);
      --border: #d9d9d9;
      --thead-bg: #2c3e50;
      --thead-fg: #ffffff;
      --row-border: #f0f0f0;
      --total-bg: #f5f5f5;
      --accent-border: #2c3e50;
      --complaint-bg: #f6ffed;
      --complaint-border: #b7eb8f;
      --complaint-text: #135200;
    }
    html[data-theme="dark"] {
      color-scheme: dark;
      --bg: #141414;
      --surface: #1f1f1f;
      --text: rgba(255, 255, 255, 0.88);
      --muted: rgba(255, 255, 255, 0.45);
      --border: #424242;
      --thead-bg: #1d3557;
      --thead-fg: #e8f4fc;
      --row-border: #303030;
      --total-bg: #262626;
      --accent-border: #4a90d9;
      --complaint-bg: #162312;
      --complaint-border: #274916;
      --complaint-text: #95de64;
    }
    html[data-theme="light"] {
      color-scheme: light;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 12px 14px 20px;
      font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
      font-size: 13px;
      line-height: 1.45;
      background: var(--bg);
      color: var(--text);
    }
    .meta {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 14px;
      padding-bottom: 12px;
      border-bottom: 1px solid var(--border);
    }
    .meta-line {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 16px;
      align-items: baseline;
      font-size: 12px;
      color: var(--text);
    }
    .meta-k {
      color: var(--muted);
      font-weight: 500;
      margin-right: 4px;
    }
    .complaint {
      margin: 0 0 14px;
      padding: 10px 12px;
      border-radius: 6px;
      border: 1px solid var(--complaint-border);
      background: var(--complaint-bg);
      color: var(--complaint-text);
      font-size: 12px;
    }
    .complaint strong {
      display: block;
      margin-bottom: 4px;
      color: var(--text);
    }
    table {
      width: 100%;
      border-collapse: collapse;
      background: var(--surface);
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid var(--border);
    }
    th {
      text-align: left;
      padding: 10px 8px;
      background: var(--thead-bg);
      color: var(--thead-fg);
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
    th.num, td.num { text-align: right; }
    th.kdv, td.kdv { text-align: center; }
    td {
      padding: 8px;
      border-bottom: 1px solid var(--row-border);
      vertical-align: top;
    }
    td.desc { word-break: break-word; }
    td.type { white-space: nowrap; font-size: 12px; color: var(--muted); }
    tbody tr:last-child td { border-bottom: none; }
    .total-row td {
      font-weight: 600;
      background: var(--total-bg);
      border-top: 2px solid var(--accent-border);
      border-bottom: none;
      padding-top: 10px;
      padding-bottom: 10px;
    }
    .total-row .label { text-align: right; }
    .empty {
      text-align: center;
      padding: 24px;
      color: var(--muted);
    }
  </style>
</head>
<body>
  <div class="meta">
    <div class="meta-line">${metaParts.join(' · ')}</div>
    <div class="meta-line">${whoParts.join(' · ')}</div>
  </div>
  ${complaintText ? `<div class="complaint"><strong>Müşteri şikayetleri</strong>${complaintText}</div>` : ''}
  ${
    tableLines.length === 0 && !hasOrderDiscount(orderLines)
      ? '<p class="empty">Bu fişte satır yok.</p>'
      : `<table>
    <thead>
      <tr>
        <th style="width:36px">#</th>
        <th style="width:88px">Tür</th>
        <th>Kalem</th>
        <th style="width:72px" class="num">Adet</th>
        <th style="width:88px" class="num">Birim</th>
        <th style="width:52px" class="kdv">KDV</th>
        <th style="width:100px" class="num">KDV hariç</th>
        <th style="width:100px" class="num">KDV dahil</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      ${buildModalTableFooterRow(orderLines, fmt)}
    </tbody>
  </table>
  ${buildDiscountSummaryBelowTable(orderLines, fmt)}`
  }
</body>
</html>`;
}
